import {
  HOVER_FOCUSED_OPACITY,
  HOVER_LINE_DIMMED_FILTER,
  HOVER_LINE_DIMMED_OPACITY,
  HOVER_LINE_FOCUSED_STROKE_WIDTH,
  HOVER_LINE_TRANSITION_CSS,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'

/**
 * Imperative hover emphasis for line-chart series, shared by
 * `useChartHoverDimming` and ask-yogi's `Chart` so line charts never re-render
 * on hover.
 *
 * Recharts drops `data-*` props on `<Line>`/`<Area>`, so the dot `<circle>`s
 * the chart renders carry `data-line-name`; each dot's closest
 * `.recharts-line` / `.recharts-area` `<g>` is the series group, whose styles
 * cascade to the curve, dots, and value labels.
 *
 * The focused series needs more than surviving the dim: with many interleaved
 * series, a merely-dimmed crowd still buries the hovered line. So the rest are
 * hard-dimmed and desaturated, while the focused group is thickened and
 * re-appended to its parent so it paints above every dimmed sibling (SVG
 * paint order is DOM order; there is no z-index). The
 * pre-move position is remembered so un-hovering restores the original
 * stacking. The focused series' hidden hover-only value labels (see
 * CustomLineLabel's hoverReveal) are revealed for the duration of the hover.
 */
export function applyLineHoverEmphasis(
  root: ParentNode,
  hoveredSeriesName: string | null,
): void {
  const groups: Array<{ group: SVGGElement; name: string | null }> = []
  const seen = new Set<Element>()
  root
    .querySelectorAll<SVGCircleElement>('circle[data-line-name]')
    .forEach((dot) => {
      const group = dot.closest<SVGGElement>('.recharts-line, .recharts-area')
      if (!group || seen.has(group)) return
      seen.add(group)
      groups.push({ group, name: dot.getAttribute('data-line-name') })
    })

  restoreOriginalPaintOrder(groups.map(({ group }) => group))

  const focusedGroups: SVGGElement[] = []
  groups.forEach(({ group, name }) => {
    const curve = group.querySelector<SVGPathElement>('path.recharts-curve')
    if (!hoveredSeriesName) {
      // Leave the transition in place so the fade back animates.
      group.style.opacity = ''
      group.style.filter = ''
      curve?.style.removeProperty('stroke-width')
      setHoverLabelsRevealed(group, false)
      return
    }
    group.style.transition = HOVER_LINE_TRANSITION_CSS
    if (name === hoveredSeriesName) {
      group.style.opacity = String(HOVER_FOCUSED_OPACITY)
      group.style.filter = ''
      curve?.style.setProperty(
        'stroke-width',
        String(HOVER_LINE_FOCUSED_STROKE_WIDTH),
      )
      setHoverLabelsRevealed(group, true)
      focusedGroups.push(group)
    } else {
      group.style.opacity = String(HOVER_LINE_DIMMED_OPACITY)
      group.style.filter = HOVER_LINE_DIMMED_FILTER
      curve?.style.removeProperty('stroke-width')
      setHoverLabelsRevealed(group, false)
    }
  })
  raiseToTop(focusedGroups)
}

/**
 * Reveal or re-hide the group's hover-revealable value labels. Every
 * multi-series CustomLineLabel carries a data-hover-reveal marker; the
 * hidden-until-hover ones render with attribute opacity 0, which the inline
 * style override beats while revealed. Labels hidden by the occlusion pass
 * (data-culled + inline visibility, see PointLabelCullingLayer) also reveal
 * while their series is hovered and return to their culled state after.
 */
function setHoverLabelsRevealed(group: SVGGElement, revealed: boolean): void {
  group
    .querySelectorAll<SVGTextElement>('text[data-hover-reveal]')
    .forEach((label) => {
      label.style.transition = HOVER_TRANSITION_CSS
      if (revealed) {
        label.style.opacity = String(HOVER_FOCUSED_OPACITY)
        label.style.visibility = 'visible'
        // Surfaces that re-render mid-hover (Ask Yogi's conversation state)
        // re-run the occlusion pass while the hover holds; this marker tells
        // it to leave revealed labels visible. See PointLabelCullingLayer.
        label.setAttribute('data-hover-revealed', '')
      } else {
        label.removeAttribute('data-hover-revealed')
        label.style.removeProperty('opacity')
        label.style.visibility = label.hasAttribute('data-culled')
          ? 'hidden'
          : ''
      }
    })
}

/** dataset key remembering a raised group's pre-move child index. */
const ORIGINAL_INDEX_KEY = 'hoverOriginalIndex'

function raiseToTop(groups: SVGGElement[]): void {
  // Record every pre-move index before the first move mutates the order —
  // a dual-axis series raises two groups (area + line), and recording as we
  // move would capture the second group's index one slot stale.
  groups.forEach((group) => {
    const parent = group.parentElement
    if (!parent) return
    if (group.dataset[ORIGINAL_INDEX_KEY] == null) {
      group.dataset[ORIGINAL_INDEX_KEY] = String(
        Array.prototype.indexOf.call(parent.children, group),
      )
    }
  })
  groups.forEach((group) => group.parentElement?.appendChild(group))
}

/**
 * Reinsert previously-raised groups at their remembered child index, in
 * ascending index order so each earlier reinsertion realigns the positions
 * the later ones expect.
 */
function restoreOriginalPaintOrder(groups: SVGGElement[]): void {
  groups
    .filter((group) => group.dataset[ORIGINAL_INDEX_KEY] != null)
    .map((group) => ({
      group,
      index: Number(group.dataset[ORIGINAL_INDEX_KEY]),
    }))
    .sort((a, b) => a.index - b.index)
    .forEach(({ group, index }) => {
      delete group.dataset[ORIGINAL_INDEX_KEY]
      const parent = group.parentElement
      if (!parent) return
      const reference = parent.children[index] ?? null
      if (reference !== group) {
        parent.insertBefore(group, reference)
      }
    })
}
